import { OperationMS } from '../../types';
import { CommandeAnalyse, TechNotes } from './types';
import { computeNoteGlobale } from './notationTechnique';
import { scoreTotal, resolveNoteFinanciere, buildFinancialPool, FinancialOffer } from './scoring';
import { isOffreValide } from './statutOffre';

// Attribution = source UNIQUE du retenu. Le legacy calculait le gagnant a deux endroits
// divergents (reduce-max mort + getBestPrestataire persiste) ; ici une seule fonction.

const commandesAnalyse = (operation: OperationMS): CommandeAnalyse[] =>
  (operation.commandes ?? []) as CommandeAnalyse[];

// Score total d'une commande (technique 0.55 + financier 0.45), notes editees + financier backend.
export const commandeScoreTotal = (
  commande: CommandeAnalyse,
  techNotes: TechNotes,
  pool: ReadonlyArray<FinancialOffer>,
): number => {
  const tech = computeNoteGlobale(techNotes[String(commande.id)]);
  const fin = resolveNoteFinanciere(commande, pool);
  return scoreTotal(tech, fin);
};

// Meilleur disant parmi les offres EN LICE : max du score total. Egalite -> 1er rencontre
// (indice le plus bas), comme le legacy (comparaison stricte `>`). Renvoie commande.id ou null.
export const computeBestCommandeId = (operation: OperationMS, techNotes: TechNotes): number | null => {
  const commandes = commandesAnalyse(operation);
  if (!commandes.length) return null;
  const pool = buildFinancialPool(commandes);

  let bestId: number | null = null;
  let bestScore = -1;
  commandes.forEach((commande) => {
    if (!isOffreValide(commande.id_status)) return;
    const score = commandeScoreTotal(commande, techNotes, pool);
    if (score > bestScore) {
      bestScore = score;
      bestId = commande.id ?? null;
    }
  });
  return bestId;
};

// Retenu effectif, precedence unique : id_commande persiste -> selection manuelle ->
// meilleur disant calcule. Consomme par le payload (`selected`) ET l'affichage : ils ne
// peuvent plus diverger.
export const resolveWinnerId = (
  operation: OperationMS,
  techNotes: TechNotes,
  manualSelection?: number | null,
): number | null => {
  if (operation.id_commande !== undefined && operation.id_commande !== null) {
    return operation.id_commande;
  }
  if (manualSelection !== undefined && manualSelection !== null) return manualSelection;
  return computeBestCommandeId(operation, techNotes);
};

// Rang 1-based d'une commande, calcule sur les offres VALIDES triees par score decroissant
// (corrige le legacy qui rangeait sur le tableau non filtre). Rang n.1 == meilleur disant.
export const getClassement = (
  operation: OperationMS,
  techNotes: TechNotes,
  commandeId: number | undefined,
): number => {
  if (commandeId === undefined) return 0;
  const commandes = commandesAnalyse(operation);
  const pool = buildFinancialPool(commandes);
  const ranked = commandes
    .filter((c) => isOffreValide(c.id_status))
    .map((c) => ({ id: c.id, score: commandeScoreTotal(c, techNotes, pool) }))
    .sort((a, b) => b.score - a.score);
  const index = ranked.findIndex((r) => r.id === commandeId);
  return index === -1 ? 0 : index + 1;
};
